import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarDays } from "react-icons/fa6";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaUsers } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import {
  Share2,
  MessageCircle,
  LayoutDashboard,
  Globe,
  Link as LinkIcon,
} from "lucide-react";
import { MapPin, Users, BedDouble, Bath, Building2 } from "lucide-react";
import { API_URL } from "../../Config";
import Loader from "../Loader/Loader";
import { createPortal } from "react-dom";

// const buildWebsiteUrl = (property) => {
//   const isHotel = property.propertytype === "Hotel";
//   return isHotel
//     ? `https://zorbastays.com/Individualhotel/${property.id}/Hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}`
//     : `https://zorbastays.com/IndividualProperty/${property.id}/${encodeURIComponent(property.propertytype)}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/${property.maxadult}/${property.maxbed}/${property.maxbath || 0}`;
// };
const buildWebsiteUrl = (property) => {
  const isHotel = property.propertytype === "Hotel";
  return isHotel
    ? `https://zorbastays.com/property-details/${property.id}/hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/guest=0/bedroom=0/bathroom=0`
    : `https://zorbastays.com/property-details/${property.id}/villa/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/guest=${property.maxadult}/bedroom=${property.maxbed}/bathroom=${property.maxbath || 0}`;
};
// NOTE: confirm the exact query param your dashboard edit page expects —
// this assumes ?id=<propertyId> based on EditPropertyDetailElementor.
const buildDashboardUrl = (property) =>
  `/EditPropertyDetailElementor?id=${property.id}&mode=view`;
const buildWhatsAppShareUrl = (property) => {
  const websiteUrl = buildWebsiteUrl(property);
  const message = `Check out ${property.propertyname} in ${property.location}, ${property.destination}: ${websiteUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
};

const ShareMenu = ({ property, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  if (!property) return null;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 224, // 224px = w-56 menu width, right-aligned
      });
    }
    setOpen((prev) => !prev);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildWebsiteUrl(property));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <div className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="text-gray-400 hover:text-[#1B2826] transition-colors"
        title="Share"
      >
        <Share2 size={16} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
            }}
            className="w-56 bg-white border rounded-lg shadow-lg z-[9999] py-1 text-sm"
          >
            <a
              href={buildWhatsAppShareUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <MessageCircle size={15} />
              Share on WhatsApp
            </a>

            <a
              href={buildDashboardUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <LayoutDashboard size={15} />
              Open in dashboard
            </a>

            <a
              href={buildWebsiteUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <Globe size={15} />
              Open on website
            </a>

            <button
              type="button"
              onClick={() => {
                handleCopyLink();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700 text-left"
            >
              <LinkIcon size={15} />
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};
const SearchProperty = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("");
  const [guestCount, setGuestCount] = useState("");

  const [properties, setProperties] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // blockedMap: { [propertyname]: { airbnb: Set<string>, db: Set<string> } }
  const [blockedMap, setBlockedMap] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  // { [propertyId]: { "YYYY-MM-DD": price } }, from property_price_global
  const [priceMap, setPriceMap] = useState({});
  const fetchedMonthsRef = useRef(new Set());

  useEffect(() => {
    // Always fetch the current month; add every month the selected range touches
    const months = new Map(); // "YYYY-MM" -> { year, month }
    const addMonth = (d) => {
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      months.set(key, { year: d.getFullYear(), month: d.getMonth() + 1 });
    };

    addMonth(new Date());
    if (checkIn) {
      const cur = new Date(checkIn.getFullYear(), checkIn.getMonth(), 1);
      const last = checkOut || checkIn;
      while (cur <= last) {
        addMonth(cur);
        cur.setMonth(cur.getMonth() + 1);
      }
    }

    months.forEach(async ({ year, month }, key) => {
      if (fetchedMonthsRef.current.has(key)) return;
      fetchedMonthsRef.current.add(key);
      try {
        const res = await axios.get(
          `${API_URL}/property-prices-by-month?year=${year}&month=${month}`,
        );
        if (res.data.success) {
          setPriceMap((prev) => {
            const next = { ...prev };
            for (const [id, dates] of Object.entries(res.data.prices || {})) {
              next[id] = { ...(next[id] || {}), ...dates };
            }
            return next;
          });
        }
      } catch (err) {
        fetchedMonthsRef.current.delete(key); // allow retry
        console.error("Failed to fetch prices:", err);
      }
    });
  }, [checkIn, checkOut]);
  // ── Fetch properties (with images) ──
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoadingProperties(true);
        const res = await axios.get(`${API_URL}/getAllPropertiesWithImages`);
        if (res.data.success) {
          setProperties(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch properties:", err);
      } finally {
        setLoadingProperties(false);
      }
    };
    fetchProperties();
  }, []);

  // ── Fetch dynamic locations ──
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const res = await axios.get(
          `${API_URL}/getPropertyLocationsAndDestinations`,
        );
        if (res.data.success) {
          setAllLocations(res.data.location || []);
          setDestinations(res.data.destination || []);
        }
      } catch (err) {
        console.error("Failed to fetch locations:", err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);
  // ── Filter by search / location / guests ──
  const filteredProperties = useMemo(() => {
    return properties.filter((p) => {
      const matchesSearch =
        !searchQuery ||
        p.propertyname?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDestination =
        !selectedDestination || p.destination === selectedDestination;

      const matchesLocation =
        !selectedLocation || p.location === selectedLocation;

      const matchesGuests =
        !guestCount ||
        Number(p.maxadult || 0) + Number(p.maxchild || 0) >= Number(guestCount);

      return (
        matchesSearch && matchesDestination && matchesLocation && matchesGuests
      );
    });
  }, [
    properties,
    searchQuery,
    selectedDestination,
    selectedLocation,
    guestCount,
  ]);

  const locations = useMemo(() => {
    if (!selectedDestination) return allLocations;

    const scoped = [
      ...new Set(
        properties
          .filter((p) => p.destination === selectedDestination)
          .map((p) => p.location)
          .filter(Boolean),
      ),
    ].sort();

    return scoped.length > 0 ? scoped : allLocations;
  }, [selectedDestination, properties, allLocations]);
  // ── Fetch blocked dates for the currently filtered/visible properties ──
  useEffect(() => {
    if (filteredProperties.length === 0) return;

    const controller = new AbortController();

    const fetchBlockedForVisible = async () => {
      setCheckingAvailability(true);
      const newMap = {};

      await Promise.all(
        filteredProperties.map(async (p) => {
          try {
            const res = await axios.get(
              `${API_URL}/blocked-dates/${encodeURIComponent(p.propertyname)}`,
              { signal: controller.signal },
            );
            if (res.data.success !== false) {
              newMap[p.propertyname] = {
                airbnb: new Set(res.data.airbnbblockeddates || []),
                db: new Set(res.data.dbblockeddates || []),
              };
            }
          } catch (err) {
            if (err.name !== "CanceledError") {
              newMap[p.propertyname] = { airbnb: new Set(), db: new Set() };
            }
          }
        }),
      );

      setBlockedMap((prev) => ({ ...prev, ...newMap }));
      setCheckingAvailability(false);
    };

    fetchBlockedForVisible();
    return () => controller.abort();
  }, [filteredProperties]);

  // ── Determine sold-out status for the selected date range ──
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const isSoldOutForRange = (propertyname) => {
    if (!checkIn || !checkOut) return false; // no dates picked — don't show sold out

    const blocked = blockedMap[propertyname];
    if (!blocked) return false; // not yet checked — assume available

    const allBlocked = new Set([...blocked.airbnb, ...blocked.db]);

    const current = new Date(checkIn);
    const end = new Date(checkOut);
    while (current < end) {
      if (allBlocked.has(formatDate(current))) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };
  // Returns { nightly, total, nights } or null if no price is available
  // Global price (property_price_global) first, then currentprice as fallback
  const getPriceInfo = (villa) => {
    const globalPrices = priceMap[villa.id] || {};
    const fallback = Number(villa.currentprice) || 0;

    const priceFor = (date) => {
      const global = Number(globalPrices[formatDate(date)]);
      if (global) return { value: global, isGlobal: true };
      if (fallback) return { value: fallback, isGlobal: false };
      return null;
    };

    // Range selected -> average nightly price across the stay
    if (checkIn && checkOut) {
      const current = new Date(checkIn);
      const end = new Date(checkOut);
      let total = 0;
      let nights = 0;
      let allGlobal = true;

      while (current < end) {
        const p = priceFor(current);
        if (!p) return null; // no global and no fallback for this night
        total += p.value;
        if (!p.isGlobal) allGlobal = false;
        nights++;
        current.setDate(current.getDate() + 1);
      }
      return nights
        ? {
            nightly: Math.round(total / nights),
            total,
            nights,
            isGlobal: allGlobal,
          }
        : null;
    }

    // No range -> today's price
    const p = priceFor(new Date());
    return p
      ? { nightly: p.value, total: p.value, nights: 1, isGlobal: p.isGlobal }
      : null;
  };
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("");
    setSelectedLocation("");
    setGuestCount("");
    setCheckIn(null);
    setCheckOut(null);
  };
  return (
    <div className="mt-8">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search bar */}
        <div className="p-2 overflow-hidden h-11 w-[120px] sm:w-[250px] bg-white border border-gray-300 rounded-lg flex group items-center focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <div className="flex items-center justify-center fill-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z"></path>
            </svg>
          </div>

          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-none outline-none text-sm bg-transparent w-full font-normal px-2 focus:ring-0"
          />

          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="text-gray-500 hover:text-black text-lg font-bold px-1"
            >
              ×
            </button>
          )}
        </div>
        {/* Dynamic Destination dropdown */}
        <div className="flex items-center gap-0 h-11 border border-gray-300 rounded-lg pl-2 bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <FaMapMarkerAlt className="text-gray-400" size={15} />

          <select
            value={selectedDestination}
            onChange={(e) => {
              setSelectedDestination(e.target.value);
              setSelectedLocation(""); // reset location when destination changes
            }}
            disabled={loadingLocations}
            className="h-full bg-transparent border-none outline-none focus:ring-0 text-sm cursor-pointer pr-2"
          >
            <option value="">
              {loadingLocations
                ? "Loading destinations..."
                : "All Destinations"}
            </option>
            {destinations.map((dest) => (
              <option key={dest} value={dest}>
                {dest}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic Location dropdown */}
        <div className="flex items-center gap-0 h-11 border border-gray-300 rounded-lg pl-2 bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <FaMapMarkerAlt className="text-gray-400" size={15} />

          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            disabled={loadingLocations}
            className="h-full bg-transparent border-none outline-none focus:ring-0 text-sm cursor-pointer pr-2"
          >
            <option value="">
              {loadingLocations ? "Loading locations..." : "All Locations"}
            </option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-0 border border-gray-300 rounded-lg pl-2 py-1 max-w-[210px] bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <FaCalendarDays className="text-gray-400" size={15} />

          <DatePicker
            selected={checkIn}
            minDate={new Date()}
            onChange={(date) => {
              setCheckIn(date);
              setCheckOut(null); // reset checkout if checkin changes
            }}
            dateFormat="dd MMM yyyy"
            placeholderText="Check In"
            className="text-sm cursor-pointer caret-transparent bg-transparent border-none focus:outline-none text-left focus:ring-0"
          />
        </div>

        <div className="flex items-center gap-0 border border-gray-300 rounded-lg pl-2 py-1 max-w-[210px] bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <FaCalendarDays className="text-gray-400" size={15} />

          <DatePicker
            selected={checkOut}
            onChange={(date) => setCheckOut(date)}
            minDate={checkIn || new Date()}
            disabled={!checkIn}
            dateFormat="dd MMM yyyy"
            placeholderText={checkIn ? "Check Out" : "Check-in first"}
            className="text-sm cursor-pointer caret-transparent bg-transparent border-none focus:outline-none text-left focus:ring-0 disabled:cursor-not-allowed"
          />
        </div>

        <div className="flex items-center gap-0 h-11 w-[140px] border border-gray-300 rounded-lg px-2 bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black">
          <FaUsers className="text-gray-400" size={20} />

          <input
            type="text"
            placeholder="No. of Guests"
            value={guestCount}
            onChange={(e) => setGuestCount(e.target.value.replace(/\D/g, ""))}
            className="w-full bg-transparent border-none outline-none text-sm focus:ring-0"
          />
        </div>

        <MdCancel
          onClick={clearFilters}
          className="text-gray-400 hover:text-green-900 cursor-pointer"
          size={16}
        />
      </div>

      {checkingAvailability && (
        <p className="mt-3 text-xs text-gray-400">Checking availability…</p>
      )}

      <div className="mt-8">
        {loadingProperties ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <Loader fullScreen={false} label="Loading properties..." />
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            No properties match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredProperties.map((villa) => {
              const soldOut = isSoldOutForRange(villa.propertyname);
              const priceInfo = getPriceInfo(villa);
              const price = priceInfo?.nightly || 0;
              // const defaultPrice = Number(villa.defaultprice) || 0;
              const thumbnail =
                (villa.images || [])
                  .slice()
                  .sort((a, b) => (a.position || 0) - (b.position || 0))[0]
                  ?.imageurl ||
                villa.thumbnailimg ||
                "";

              return (
                <div
                  key={villa.id}
                  className={`overflow-hidden rounded-lg bg-white shadow-md transition-all duration-300 hover:shadow-custom ${
                    soldOut ? "opacity-75" : ""
                  }`}
                >
                  {/* Image */}
                  <div className="relative h-[280px] w-full bg-gray-100">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={villa.propertyname}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-gray-400 text-sm">
                        No image available
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="truncate text-lg font-medium text-[#1B2827]">
                        {villa.propertyname}
                      </h2>
                      <ShareMenu property={villa} />
                    </div>

                    {/* Location */}
                    <div className="mt-4 flex items-center gap-2 text-gray-500">
                      <MapPin className="h-5 w-5 shrink-0" />
                      <p className="truncate text-sm">
                        {[villa.location, villa.destination]
                          .filter(Boolean)
                          .join(", ") || "Location unavailable"}
                      </p>
                    </div>

                    {/* Amenities */}
                    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-3 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>{villa.maxadult ?? 0} Guests</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>{villa.maxbed ?? 0} Bedrooms</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <BedDouble className="h-4 w-4" />
                        <span>{villa.maxbed ?? 0} Beds</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Bath className="h-4 w-4" />
                        <span>{villa.maxbath ?? 0} Bathrooms</span>
                      </div>
                    </div>

                    {/* Price / Sold Out */}
                    <div className="mt-5">
                      <div className="flex items-baseline gap-2">
                        <span
                          className={`text-xl font-semibold ${
                            soldOut
                              ? "text-gray-400 line-through"
                              : "text-green-900"
                          }`}
                        >
                          ₹{price ? price.toLocaleString("en-IN") : "N/A"}
                        </span>

                        {!soldOut && (
                          <span className="text-sm font-medium text-green-900">
                            /night
                          </span>
                        )}
                        {!soldOut && priceInfo && priceInfo.nights > 1 && (
                          <p className="mt-1 text-sm text-gray-500">
                            ₹{priceInfo.total.toLocaleString("en-IN")} for{" "}
                            {priceInfo.nights} nights
                          </p>
                        )}

                        {soldOut && (
                          <span className="text-sm font-semibold text-red-600 uppercase">
                            Sold Out
                          </span>
                        )}
                      </div>

                      {/* {!soldOut &&
                        defaultPrice > 0 &&
                        defaultPrice !== price && (
                          <p className="mt-1 text-base text-gray-400 line-through">
                            ₹{defaultPrice.toLocaleString("en-IN")}
                          </p>
                        )} */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchProperty;
